/**
 * UI-local sample RuleContext (TORR RF → US) for the offline Gate demo page.
 * This lets the gate/merge screens work without Supabase (env-less run).
 * Mirrors db/seed/0001_seed.sql and scripts/demo.ts (kept in sync manually).
 *
 * Owned by B4 (src/pages). NOT a shared lib (CONTRACT §2 ownership).
 */

import type {
  Country,
  Deal,
  DealLineItem,
  Incoterm,
  Partner,
  Product,
  RuleContext,
  ShipperProfile,
} from "@/types";

const now = "2026-06-24T00:00:00.000Z";

/** Seed BRITZMEDI shipper profile (mirrors db/seed/0001_seed.sql shipper_profiles). */
export const sampleShipper: ShipperProfile = {
  id: "shipper-britzmedi",
  company_name: "BRITZMEDI Co., Ltd.",
  address:
    "No.1211, 12F, 388, Dunchon-daero, Jungwon-gu, Seongnam-si, Gyeonggi-do, Republic of Korea",
  business_reg_no: null,
  beneficiary_bank: {
    bank_name: "KOOKMIN BANK MANGPOYEOK BRANCH",
    swift: "CZNBKRSEXXX",
    account_no: "422068-11-010544",
    currency: "USD",
  },
  signature_image_ref: null,
  logo_image_ref: null,
  deal_no_pattern: "BM-YYMMDD###",
  default_export_port: "ICN",
};

const usCountry: Country = {
  id: "US",
  name_en: "United States",
  name_ko: "미국",
  default_currency: "USD",
  required_documents: [
    { doc_type: "commercial_invoice", requirement: "required" },
    { doc_type: "packing_list", requirement: "required" },
    { doc_type: "korus_co", requirement: "optional" },
    { doc_type: "awb", requirement: "external_attachment" },
  ],
  recommended_incoterms: ["FCA", "CPT", "CIP", "DAP"],
  cert_requirements: [{ scheme: "FDA", mandatory: true }],
  regulatory_notes: "IOR(CBP) 와 FDA Initial Importer 각각 필요.",
  tariff_note:
    "미국 관세 환경은 변동성이 높음. 정확 세율·특혜 적용 여부는 선적시점 통관사/CBP ACE 확인. 본 도구는 세율을 산출·고정하지 않음.",
  tariff_verified_at: "2026-06-24",
  tariff_source_url: null,
  required_importer_roles: [],
  is_sanctioned: false,
  language: "en",
  created_at: now,
  updated_at: now,
};

const torrRf: Product = {
  id: "product-torr-rf",
  model_name: "TORR RF",
  internal_code: "MTX-C1",
  description_en:
    "TORR RF — Depth-Controlled Multipolar Radiofrequency Medical Device",
  hs_code: null,
  hs_verified: false,
  country_of_origin: "KR",
  unit_price: 12000,
  currency: "USD",
  net_weight_kg: 8.5,
  gross_weight_kg: 11.2,
  box_length_cm: 60,
  box_width_cm: 45,
  box_height_cm: 40,
  cbm: 0.108,
  device_class: "Class II",
  regulatory: {
    fda_510k_number: "K212561",
    fda_fei: "3029916132",
    fda_device_listing: "MTX-C1",
    fda_listing_active: true,
    fda_product_code: "",
  },
  reg_verified_at: "2026-06-24",
  is_active: true,
  created_at: now,
};

const buyer: Partner = {
  id: "partner-abc-medical",
  company_name: "ABC Medical Inc.",
  company_name_local: null,
  country: "US",
  address: "123 Market St, San Francisco, CA",
  contact_person: "Jane Doe",
  email: "jane@abcmedical.example",
  phone: null,
  tax_vat_id: null,
  eori: null,
  roles: ["buyer", "consignee", "importer_of_record", "fda_initial_importer"],
  is_importer_of_record: true,
  is_fda_initial_importer: true,
  fda_initial_importer_fei: null,
  us_physical_address: "123 Market St, San Francisco, CA",
  grade: null,
  importer_ids: {},
  sanction_check_status: "not_checked",
  sanction_checked_at: null,
  created_at: now,
};

const fcaIncoterm: Incoterm = {
  id: "FCA",
  full_name: "Free Carrier",
  name_ko: "운송인인도",
  group_code: "F",
  transport_mode: "any",
  risk_transfer_point: "지정장소에서 운송인에게 인도 시",
  cost_split_point: "지정장소(인도 지점)",
  risk_cost_separated: false,
  export_clearance_by: "S",
  import_clearance_by: "B",
  seller_insurance_obligation: "none",
  insurance_min_coverage_pct: null,
  suitable_cargo: "any",
  seller_unloads: false,
  notes: null,
};

const deal: Deal = {
  id: "deal-demo-1",
  deal_no: "BM-260624001",
  title: "TORR RF → US / ABC Medical",
  status: "ship_prep",
  destination_country: "US",
  shipper_profile_id: "shipper-britzmedi",
  buyer_id: buyer.id,
  consignee_id: buyer.id,
  notify_id: null,
  importer_of_record_id: buyer.id,
  fda_initial_importer_id: buyer.id,
  importer_structure: "distributor",
  incoterm_id: "FCA",
  incoterm_named_place: "ICN Airport, Incheon",
  incoterm_named_place_type: "airport",
  incoterm_version: "2020",
  transport_mode: "air",
  cargo_type: "finished_goods",
  payment_term_id: "payment-tt-100-adv",
  currency: "USD",
  claim_preferential: false,
  insurance_obligation_party: "none",
  insured_amount: null,
  export_declaration_status: "none",
  fob_value_krw: null,
  payment_received: true,
  intended_use_text: null,
  tariff_ack: true,
  export_declaration_no: null,
  transport_doc_no: null,
  insurance_policy_no: null,
  lc_expiry_date: null,
  lc_latest_shipment_date: null,
  contract_id: null,
  notes: null,
  created_at: now,
  updated_at: now,
};

const lines: DealLineItem[] = [
  {
    id: "line-1",
    deal_id: deal.id,
    product_id: torrRf.id,
    item_kind: "product",
    line_no: 1,
    description_en: torrRf.description_en,
    hs_code: null,
    quantity: 2,
    unit: "units",
    unit_price: 12000,
    line_amount: 24000,
    net_weight_kg: 17,
    gross_weight_kg: 22.4,
    weight_unit: "kg",
    package_count: 2,
    package_type: "carton",
    cbm: 0.216,
    is_no_commercial_value: false,
    created_at: now,
  },
];

/** Healthy TORR RF → US context (gate should PASS). */
export function sampleContext(): RuleContext {
  return {
    deal,
    lines,
    partners: {
      buyer,
      consignee: buyer,
      importer_of_record: buyer,
      fda_initial_importer: buyer,
    },
    incoterm: fcaIncoterm,
    country: usCountry,
    products: [torrRf],
    documents: [],
  };
}

/** Intentionally broken context: FOB + air, no IOR/Initial Importer, tariff unacked. */
export function brokenContext(): RuleContext {
  const base = sampleContext();
  return {
    ...base,
    deal: {
      ...base.deal,
      incoterm_id: "FOB",
      transport_mode: "air",
      importer_of_record_id: null,
      fda_initial_importer_id: null,
      tariff_ack: false,
      export_declaration_no: null,
      transport_doc_no: null,
      insurance_policy_no: null,
      lc_expiry_date: null,
      lc_latest_shipment_date: null,
      contract_id: null,
    },
    incoterm: {
      ...fcaIncoterm,
      id: "FOB",
      full_name: "Free On Board",
      name_ko: "본선인도",
      transport_mode: "sea",
    },
    partners: {
      ...base.partners,
      importer_of_record: null,
      fda_initial_importer: null,
    },
  };
}
